import { LiveStatus, RecorderStatus, StreamValidation } from '@core/types';
import { Button, Close, Heading } from '@core/ui';
import { isExternalEncoder, PresenterStatus, resolveIngestType, useAppSelector } from '@online-webinar/store';
import { Trans, useTranslation } from 'react-i18next';
import { HealthCheckItem, Troubleshoot } from '../..';

export interface BroadcastStatusProps {
    audioDevice: string;
    broadcastState: string;
    audioScore: number;
    videoScore: number;
    onClose: () => void;
    onSelectDevices: () => void;
    videoDevice: string;
    recorderStatus: RecorderStatus;
    dataStatus: LiveStatus;
    presenterStatus: PresenterStatus;
}

export const BroadcastStatusPanel = ({
    audioDevice,
    broadcastState,
    audioScore,
    videoScore,
    onClose,
    onSelectDevices,
    videoDevice,
    recorderStatus,
    dataStatus,
    presenterStatus
}: BroadcastStatusProps) => {
    const { t } = useTranslation();

    const hasEnded = useAppSelector((state) => state.liveWebinar.hasEnded);
    const isTooEarly = useAppSelector((state) => state.liveWebinar.isTooEarly);
    const vindralChannel = useAppSelector((state) => state.liveWebinar.vindralChannel);
    const isActivePresenter = useAppSelector((state) => state.liveWebinar.isActivePresenter);
    const failedServerActions = useAppSelector((state) => state.liveWebinar.failedServerActions);
    const streamValidation = useAppSelector((state) => state.liveWebinar.streamValidation);
    const videoDeviceId = useAppSelector((state) => state.liveWebinar.videoDeviceId);

    // The verdict is read off Vindral's edge output, which exists for every broadcast - including a
    // WebRTC one, where it describes Vindral's encoding rather than anything the presenter chose.
    // It is only worth showing, and only ever blocks, when the presenter's own encoder produced it.
    // The start button derives from this same condition; a disabled button without this entry next
    // to it is a dead control with no stated reason and no override.
    const moqPublisher = useAppSelector((state) => state.liveWebinar.moqPublisher);
    const usesExternalEncoder = isExternalEncoder(resolveIngestType(videoDeviceId, moqPublisher));

    let recorderIssue = false;
    if (presenterStatus === PresenterStatus.Live && broadcastState === 'broadcasting' && recorderStatus !== RecorderStatus.Recording) {
        recorderIssue = true;
    }
    if (presenterStatus === PresenterStatus.Ready && broadcastState === 'broadcasting' && recorderStatus !== RecorderStatus.StandBy) {
        recorderIssue = true;
    }

    // Keyed on the verdict rather than chained ternaries so a verdict added later shows up here as
    // a missing case instead of silently falling through to the "could not check yet" copy.
    const encoderCopy: Record<StreamValidation, { issue: string; solution: string }> = {
        [StreamValidation.Unknown]: { issue: 'Broadcaster.Status.Encoder.Issue.Unknown', solution: 'Broadcaster.Status.Solution.Encoder.Unknown' },
        // Ok renders neither of these - HealthCheckItem shows only its label when there is no issue.
        [StreamValidation.Ok]: { issue: 'Broadcaster.Status.Encoder', solution: '' },
        [StreamValidation.BlockedAudioChannels]: { issue: 'Broadcaster.Status.Encoder.Issue.Audio', solution: 'Broadcaster.Status.Solution.Encoder.Audio' },
        [StreamValidation.BlockedVideoLevel]: { issue: 'Broadcaster.Status.Encoder.Issue.Level', solution: 'Broadcaster.Status.Solution.Encoder.Level' },
        [StreamValidation.BlockedAudioChannelsAndVideoLevel]: {
            issue: 'Broadcaster.Status.Encoder.Issue.AudioAndLevel',
            solution: 'Broadcaster.Status.Solution.Encoder.AudioAndLevel'
        }
    };

    const encoderIssueLabel = t(encoderCopy[streamValidation].issue);
    const encoderSolution = encoderCopy[streamValidation].solution ? t(encoderCopy[streamValidation].solution) : '';

    return (
        <div className="bg-white rounded-md px-6 pt-4 pb-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between ">
                <Heading variant="h2">{t('Broadcaster.Status.Heading')}</Heading>
                <Button onClick={onClose} iconbtn classNames="-me-3">
                    <Close classNames="size-6 stroke-current" />
                </Button>
            </div>
            <ul className="flex flex-col gap-4">
                <HealthCheckItem hasIssue={!videoDevice} label={t('Broadcaster.Status.Camera')} issueLabel={t('Broadcaster.Status.Camera.Issue')}>
                    <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                    <Button onClick={onSelectDevices} className="underline decoration-primary decoration-2 underline-offset-4">
                        {t('Broadcaster.Status.Solution.Button.Reconnect')}
                    </Button>{' '}
                </HealthCheckItem>
                <HealthCheckItem hasIssue={!audioDevice} label={t('Broadcaster.Status.Mic')} issueLabel={t('Broadcaster.Status.Mic.Issue')}>
                    <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                    <Button onClick={onSelectDevices} className="underline decoration-primary decoration-2 underline-offset-4">
                        {t('Broadcaster.Status.Solution.Button.Reconnect')}
                    </Button>
                </HealthCheckItem>
                <HealthCheckItem
                    hasIssue={dataStatus !== LiveStatus.Live}
                    label={t('Broadcaster.Status.Server')}
                    issueLabel={t('Broadcaster.Status.Server.Issue')}
                >
                    <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                    {t('Broadcaster.Status.Solution.Refresh')}
                </HealthCheckItem>
                {!hasEnded && isActivePresenter && (
                    <HealthCheckItem
                        hasIssue={broadcastState !== 'broadcasting'}
                        label={t('Broadcaster.Status.VideoServer')}
                        issueLabel={t('Broadcaster.Status.VideoServer.Issue')}
                    >
                        <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                        {isTooEarly && t('Broadcaster.Status.Solution.WaitForTimeslot')}
                        {broadcastState !== 'initializing' && !!vindralChannel?.channelId && t('Broadcaster.Status.Solution.Refresh')}
                        {broadcastState === 'initializing' && t('Broadcaster.Status.Solution.Initializing')}
                        {broadcastState === 'error' && t('Broadcaster.Status.Solution.Refresh')}
                    </HealthCheckItem>
                )}
                {broadcastState === 'broadcasting' && (
                    <HealthCheckItem
                        hasIssue={audioScore + videoScore != 20}
                        label={t('Broadcaster.Status.VideoServer.Optimal')}
                        issueLabel={t('Broadcaster.Status.VideoServer.Unstable')}
                    >
                        <span className="block text-xs mb-1 ">{t('Broadcaster.Status.VideoServer.Score')}</span>
                        <ul className="list-disc list-inside mb-2">
                            <li>
                                <Trans i18nKey="Broadcaster.Status.VideoServer.Score.Audio" values={{ audioScore }} />
                            </li>
                            <li>
                                <Trans i18nKey="Broadcaster.Status.VideoServer.Score.Video" values={{ videoScore }} />
                            </li>
                        </ul>
                        {t('Broadcaster.Status.VideoServer.Score.Text')}
                    </HealthCheckItem>
                )}
                {broadcastState === 'broadcasting' && (
                    <HealthCheckItem hasIssue={recorderIssue} label={t('Broadcaster.Status.Recorder')} issueLabel={t('Broadcaster.Status.Recorder.Issue')}>
                        <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                        {t('Broadcaster.Status.Solution.Refresh')}
                    </HealthCheckItem>
                )}
                {usesExternalEncoder && (
                    <HealthCheckItem hasIssue={streamValidation !== StreamValidation.Ok} label={t('Broadcaster.Status.Encoder')} issueLabel={encoderIssueLabel}>
                        <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                        {encoderSolution}
                    </HealthCheckItem>
                )}
                {failedServerActions && failedServerActions.length > 0 && (
                    <HealthCheckItem hasIssue={true} label={t('Broadcaster.Status.ServerActions')} issueLabel={t('Broadcaster.Status.ServerActions.Issue')}>
                        <span className="block text-xs mb-1 ">{t('Broadcaster.Status.PossibleSolution')}</span>
                        {t('Broadcaster.Status.Solution.Refresh')}
                    </HealthCheckItem>
                )}
            </ul>
            {(!videoDevice || !audioDevice || dataStatus !== LiveStatus.Live) && <Troubleshoot link="https://www.onlinewebinar.com/support/#audio_video" />}
        </div>
    );
};
